import React, { useState, useEffect } from 'react';
import prayerService from '../../api/prayerService';
import jewishHolidaysService from '../../api/jewishHolidaysService';
import PrayerEditModal from './PrayerEditModal';
import ConfirmationModal from '../ConfirmationModal';
import DayAgendaPanel from './DayAgendaPanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { sortItemsByTime } from '../../utils/timeUtils';
import '../../styles/group/Prayer.css';

const Prayer = ({ groupId, searchQuery, isAdmin, highlightedQuery, onDataLoad, filteredData }) => {
  // Week navigation state
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    // Start from the most recent Sunday
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - dayOfWeek);
    sunday.setHours(0, 0, 0, 0);
    return sunday;
  });

  // Jewish holidays state
  const [jewishHolidays, setJewishHolidays] = useState([]);
  const [miniCalendarHolidays, setMiniCalendarHolidays] = useState([]);
  const [selectedHolidayFilters, setSelectedHolidayFilters] = useState(() => {
    const saved = localStorage.getItem('prayerHolidayFilters');
    return saved ? JSON.parse(saved) : ['major', 'minor', 'fast', 'rosh_chodesh', 'shabbat', 'modern'];
  });
  const [showHolidayFilter, setShowHolidayFilter] = useState(false);
  const [filterPanelMounted, setFilterPanelMounted] = useState(false);

  // Function to get today's day name
  const getTodayDayName = () => {
    const today = new Date();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[today.getDay()];
    
    // Map Saturday to Shabbat
    return dayName === 'Saturday' ? 'Shabbat' : dayName;
  };

  // Function to get date in MM/DD format for a given day of the week based on current week
  const getDateForDay = (dayName, weekStart = currentWeekStart) => {
    const dayNameToIndex = {
      'Sunday': 0,
      'Monday': 1,
      'Tuesday': 2,
      'Wednesday': 3,
      'Thursday': 4,
      'Friday': 5,
      'Shabbat': 6
    };
    
    const targetDayIndex = dayNameToIndex[dayName];
    const targetDate = new Date(weekStart);
    targetDate.setDate(weekStart.getDate() + targetDayIndex);
    
    const month = (targetDate.getMonth() + 1).toString().padStart(2, '0');
    const day = targetDate.getDate().toString().padStart(2, '0');
    const year = targetDate.getFullYear();
    
    return {
      display: `${month}/${day}`,
      full: targetDate,
      iso: `${year}-${month}-${day}`
    };
  };

  // Get full date object for a day name
  const getFullDateForDay = (dayName) => {
    return getDateForDay(dayName).full;
  };

  // Navigation functions
  const goToPreviousWeek = () => {
    if (isAnimating) return;
    setSlideDirection('right');
    setIsAnimating(true);
    
    // Wait for slide-out animation
    setTimeout(() => {
      const newWeekStart = new Date(currentWeekStart);
      newWeekStart.setDate(currentWeekStart.getDate() - 7);
      setCurrentWeekStart(newWeekStart);
      setSlideDirection('right-in');
      
      // Reset animation state after slide-in
      setTimeout(() => {
        setIsAnimating(false);
        setSlideDirection(null);
      }, 250);
    }, 250);
  };

  const goToNextWeek = () => {
    if (isAnimating) return;
    setSlideDirection('left');
    setIsAnimating(true);
    
    // Wait for slide-out animation
    setTimeout(() => {
      const newWeekStart = new Date(currentWeekStart);
      newWeekStart.setDate(currentWeekStart.getDate() + 7);
      setCurrentWeekStart(newWeekStart);
      setSlideDirection('left-in');
      
      // Reset animation state after slide-in
      setTimeout(() => {
        setIsAnimating(false);
        setSlideDirection(null);
      }, 250);
    }, 250);
  };

  const goToCurrentWeek = () => {
    if (isAnimating) return;
    const today = new Date();
    const dayOfWeek = today.getDay();
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - dayOfWeek);
    sunday.setHours(0, 0, 0, 0);
    
    // Determine slide direction based on whether we're going forward or backward
    const isGoingForward = sunday.getTime() > currentWeekStart.getTime();
    const direction = isGoingForward ? 'left' : 'right';
    setSlideDirection(direction);
    setIsAnimating(true);
    
    setTimeout(() => {
      setCurrentWeekStart(sunday);
      setSlideDirection(`${direction}-in`);
      
      setTimeout(() => {
        setIsAnimating(false);
        setSlideDirection(null);
      }, 250);
    }, 250);
  };

  // Format week range for display
  const getWeekRangeDisplay = () => {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(currentWeekStart.getDate() + 6);
    
    const formatDate = (date) => {
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      return `${month} ${day}`;
    };
    
    return `${formatDate(currentWeekStart)} - ${formatDate(weekEnd)}`;
  };

  // Check if current week is this week
  const isCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const thisWeekSunday = new Date(today);
    thisWeekSunday.setDate(today.getDate() - dayOfWeek);
    thisWeekSunday.setHours(0, 0, 0, 0);
    
    return currentWeekStart.getTime() === thisWeekSunday.getTime();
  };

  const [selectedDay, setSelectedDay] = useState(getTodayDayName()); // Default to today
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [selectedDayForModal, setSelectedDayForModal] = useState(null);
  const [prayerSchedules, setPrayerSchedules] = useState({});
  const [weekCache, setWeekCache] = useState({}); // Cache for multiple weeks
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasApiError, setHasApiError] = useState(false); // Track if we had API errors
  const [hasUserInteracted, setHasUserInteracted] = useState(false); // Track if user has manually selected a day
  const [slideDirection, setSlideDirection] = useState(null); // 'left' or 'right' for animation
  const [isAnimating, setIsAnimating] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showMiniCalendar, setShowMiniCalendar] = useState(false);
  const [miniCalendarMonth, setMiniCalendarMonth] = useState(new Date());
  const [editModal, setEditModal] = useState({
    isOpen: false,
    prayerType: null,
    prayerTimes: []
  });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'default',
    onConfirm: null
  });

  // Days of the week
  const getDaysOfWeek = () => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Shabbat'];
    
    return dayNames.map((dayName) => {
      return {
        name: dayName,
        date: getDateForDay(dayName)
      };
    });
  };

  const daysOfWeek = getDaysOfWeek();

  // Helper function to get a week key (YYYY-MM-DD of Sunday)
  const getWeekKey = (weekStart) => {
    return weekStart.toISOString().split('T')[0];
  };

  // Helper function to calculate week start for a given offset from current week
  const getWeekStartForOffset = (offset) => {
    const weekStart = new Date(currentWeekStart);
    weekStart.setDate(currentWeekStart.getDate() + (offset * 7));
    return weekStart;
  };

  // Load multiple weeks at once for progressive loading
  const loadMultipleWeeks = async (weekOffsets, forceRefresh = false) => {
    try {
      const promises = weekOffsets.map(async (offset) => {
        const weekStart = getWeekStartForOffset(offset);
        const weekKey = getWeekKey(weekStart);
        
        // Skip if already in cache (unless force refresh)
        if (!forceRefresh && weekCache[weekKey]) {
          console.log(`📦 Week ${weekKey} already cached, skipping`);
          return { weekKey, data: weekCache[weekKey] };
        }
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        const startDate = weekStart.toISOString().split('T')[0];
        const endDate = weekEnd.toISOString().split('T')[0];
        
        console.log(`📥 Loading week ${weekKey} (${startDate} to ${endDate})${forceRefresh ? ' [FORCE REFRESH]' : ''}`);
        const response = await prayerService.getPrayerTimes(groupId, startDate, endDate);
        return { weekKey, data: response.data || {} };
      });
      
      const results = await Promise.all(promises);
      
      // Update cache with all loaded weeks
      const newCache = { ...weekCache };
      results.forEach(({ weekKey, data }) => {
        newCache[weekKey] = data;
      });
      setWeekCache(newCache);
      
      // Set current week's data to prayerSchedules
      const currentWeekKey = getWeekKey(currentWeekStart);
      const currentWeekData = results.find(r => r.weekKey === currentWeekKey);
      if (currentWeekData) {
        setPrayerSchedules(currentWeekData.data);
      } else if (newCache[currentWeekKey]) {
        setPrayerSchedules(newCache[currentWeekKey]);
      }
      
      console.log(`✅ Loaded ${results.length} weeks, cache size: ${Object.keys(newCache).length}`);
      
      // Return the new cache so caller can access updated data
      return newCache;
    } catch (error) {
      console.error('Error loading multiple weeks:', error);
      throw error;
    }
  };

  // Load prayer times from database
  const loadPrayerTimes = async () => {
    try {
      setLoading(true);
      setError(null);
      setHasApiError(false); // Reset API error state
      
      // Load current week + 3 weeks ahead + 3 weeks behind
      await loadMultipleWeeks([-3, -2, -1, 0, 1, 2, 3]);
      
      // Notify parent component about data load for search functionality
      if (onDataLoad) {
        const currentWeekKey = getWeekKey(currentWeekStart);
        const schedules = weekCache[currentWeekKey] || prayerSchedules;
        
        // Flatten all prayer times for search
        const allPrayerTimes = [];
        Object.entries(schedules).forEach(([day, daySchedule]) => {
          Object.entries(daySchedule).forEach(([prayerType, times]) => {
            times.forEach(time => {
              allPrayerTimes.push({
                ...time,
                day,
                prayerType,
                name: `${time.description} - ${prayerType}`,
                date: `${day} ${time.time}`
              });
            });
          });
        });
        onDataLoad(allPrayerTimes);
      }
    } catch (error) {
      console.error('Error loading prayer times:', error);
      setError('Please refresh the page to reload prayer schedules');
      setHasApiError(true); // Set API error state
      
      // Initialize with empty structure on error
      const emptySchedules = {};
      daysOfWeek.forEach(day => {
        emptySchedules[day.name] = {
          shacharit: [],
          mincha: [],
          maariv: []
        };
      });
      setPrayerSchedules(emptySchedules);
    } finally {
      setLoading(false);
    }
  };

  // Silent reload for background updates (doesn't show loading state)
  const reloadPrayerTimesQuietly = async () => {
    try {
      // Load 3 weeks ahead and 3 weeks behind from current week - FORCE REFRESH to get latest data
      const updatedCache = await loadMultipleWeeks([-3, -2, -1, 0, 1, 2, 3], true);
      
      // Make sure the current displayed week is updated with fresh data
      if (updatedCache) {
        const currentWeekKey = getWeekKey(currentWeekStart);
        if (updatedCache[currentWeekKey]) {
          console.log(`🔄 Updating displayed data for week ${currentWeekKey} after save`);
          setPrayerSchedules(updatedCache[currentWeekKey]);
        }
      }
    } catch (error) {
      console.error('Error reloading prayer times quietly:', error);
      // Silently fail - don't disrupt user experience
    }
  };

  // Load prayer times on component mount
  useEffect(() => {
    if (groupId) {
      loadPrayerTimes();
    }
  }, [groupId]); // Only reload on groupId change
  
  // Update displayed week when currentWeekStart changes
  useEffect(() => {
    if (groupId && currentWeekStart) {
      const currentWeekKey = getWeekKey(currentWeekStart);
      
      // If we have this week in cache, show it immediately
      if (weekCache[currentWeekKey]) {
        console.log(`📦 Using cached data for week ${currentWeekKey}`);
        setPrayerSchedules(weekCache[currentWeekKey]);
      }
      
      // Then quietly load 3 weeks ahead and behind in background
      reloadPrayerTimesQuietly();
    }
  }, [currentWeekStart]); // Reload when week changes

  // Keyboard navigation for weeks
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle arrow keys if no modal is open and not animating
      if (!editModal.isOpen && !dayModalOpen && !confirmModal.isOpen && !isAnimating) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goToPreviousWeek();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          goToNextWeek();
        } else if (e.key === 'Escape' && showMiniCalendar) {
          setShowMiniCalendar(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editModal.isOpen, dayModalOpen, confirmModal.isOpen, isAnimating, currentWeekStart, showMiniCalendar]);

  // Click outside to close mini calendar
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMiniCalendar) {
        // Check if click is outside the mini calendar
        const miniCalendar = document.getElementById('mini-calendar-dropdown');
        const calendarButton = document.getElementById('mini-calendar-button');
        
        if (miniCalendar && calendarButton && 
            !miniCalendar.contains(e.target) && 
            !calendarButton.contains(e.target)) {
          setShowMiniCalendar(false);
        }
      }
    };

    if (showMiniCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMiniCalendar]);

  // Fetch Jewish holidays for the current week
  useEffect(() => {
    const fetchHolidaysForWeek = async () => {
      try {
        // Don't fetch if no filters are selected
        if (selectedHolidayFilters.length === 0) {
          setJewishHolidays([]);
          return;
        }

        // Calculate end of week (Saturday)
        const weekEnd = new Date(currentWeekStart);
        weekEnd.setDate(currentWeekStart.getDate() + 6);
        
        const startDate = currentWeekStart.toISOString().split('T')[0];
        const endDate = weekEnd.toISOString().split('T')[0];
        
        // Fetch holidays for each selected filter and combine results
        const allHolidays = [];
        for (const filter of selectedHolidayFilters) {
          const holidays = await jewishHolidaysService.getHolidays(
            startDate, 
            endDate,
            filter
          );
          if (holidays) {
            allHolidays.push(...holidays);
          }
        }
        
        // Remove duplicates based on id
        const uniqueHolidays = Array.from(
          new Map(allHolidays.map(h => [h.id, h])).values()
        );
        
        setJewishHolidays(uniqueHolidays);
      } catch (error) {
        console.error('Error fetching Jewish holidays:', error);
        setJewishHolidays([]);
      }
    };

    fetchHolidaysForWeek();
  }, [currentWeekStart, selectedHolidayFilters]);

  // Persist holiday filters to localStorage
  useEffect(() => {
    localStorage.setItem('prayerHolidayFilters', JSON.stringify(selectedHolidayFilters));
  }, [selectedHolidayFilters]);

  // Track when filter panel is mounted to prevent animation on re-renders
  useEffect(() => {
    if (showHolidayFilter) {
      setFilterPanelMounted(false);
      const timer = setTimeout(() => setFilterPanelMounted(true), 350);
      return () => clearTimeout(timer);
    } else {
      setFilterPanelMounted(false);
    }
  }, [showHolidayFilter]);

  // Fetch holidays for mini calendar month
  useEffect(() => {
    const fetchMiniCalendarHolidays = async () => {
      try {
        if (selectedHolidayFilters.length === 0) {
          setMiniCalendarHolidays([]);
          return;
        }

        const firstDay = new Date(miniCalendarMonth.getFullYear(), miniCalendarMonth.getMonth(), 1);
        const lastDay = new Date(miniCalendarMonth.getFullYear(), miniCalendarMonth.getMonth() + 1, 0);
        
        const startDate = firstDay.toISOString().split('T')[0];
        const endDate = lastDay.toISOString().split('T')[0];
        
        const allHolidays = [];
        for (const filter of selectedHolidayFilters) {
          const holidays = await jewishHolidaysService.getHolidays(startDate, endDate, filter);
          if (holidays) {
            allHolidays.push(...holidays);
          }
        }
        
        const uniqueHolidays = Array.from(
          new Map(allHolidays.map(h => [h.id, h])).values()
        );
        
        setMiniCalendarHolidays(uniqueHolidays);
      } catch (error) {
        console.error('Error fetching mini calendar holidays:', error);
        setMiniCalendarHolidays([]);
      }
    };

    fetchMiniCalendarHolidays();
  }, [miniCalendarMonth, selectedHolidayFilters]);

  // Toggle a holiday filter on/off
  const toggleHolidayFilter = (filterValue) => {
    setSelectedHolidayFilters(prev => {
      if (prev.includes(filterValue)) {
        // Remove filter
        return prev.filter(f => f !== filterValue);
      } else {
        // Add filter
        return [...prev, filterValue];
      }
    });
  };

  // Remove a specific filter (used by chips)
  const removeFilter = (filterValue) => {
    setSelectedHolidayFilters(prev => prev.filter(f => f !== filterValue));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedHolidayFilters([]);
  };

  // Select all filters
  const selectAllFilters = () => {
    setSelectedHolidayFilters(['major', 'minor', 'fast', 'rosh_chodesh', 'shabbat', 'modern']);
  };

  // Helper function to check if a date has a holiday
  const getHolidaysForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return jewishHolidays.filter(holiday => holiday.gregorian_date === dateStr);
  };

  // Helper function to check if a date has a holiday in mini calendar
  const getMiniCalendarHolidaysForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return miniCalendarHolidays.filter(holiday => holiday.gregorian_date === dateStr);
  };

  // Touch/swipe gesture support
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd || isAnimating) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      goToNextWeek();
    } else if (isRightSwipe) {
      goToPreviousWeek();
    }
  };

  const handleDaySelect = (day) => {
    setSelectedDay(day);
    setHasUserInteracted(true); // Mark as interacted when user selects a day
  };

  // Mini calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days in the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const handleDateClick = (date) => {
    if (!date) return;
    
    // Find the Sunday of the week containing this date
    const dayOfWeek = date.getDay();
    const sunday = new Date(date);
    sunday.setDate(date.getDate() - dayOfWeek);
    sunday.setHours(0, 0, 0, 0);
    
    // Determine slide direction
    const isGoingForward = sunday.getTime() > currentWeekStart.getTime();
    const direction = isGoingForward ? 'left' : 'right';
    
    if (sunday.getTime() === currentWeekStart.getTime()) {
      // Same week, just close calendar
      setShowMiniCalendar(false);
      return;
    }
    
    setSlideDirection(direction);
    setIsAnimating(true);
    
    setTimeout(() => {
      setCurrentWeekStart(sunday);
      setSlideDirection(`${direction}-in`);
      
      setTimeout(() => {
        setIsAnimating(false);
        setSlideDirection(null);
        setShowMiniCalendar(false);
      }, 250);
    }, 250);
  };

  const goToPreviousMonth = () => {
    const newMonth = new Date(miniCalendarMonth);
    newMonth.setMonth(miniCalendarMonth.getMonth() - 1);
    setMiniCalendarMonth(newMonth);
  };

  const goToNextMonth = () => {
    const newMonth = new Date(miniCalendarMonth);
    newMonth.setMonth(miniCalendarMonth.getMonth() + 1);
    setMiniCalendarMonth(newMonth);
  };

  const isDateInCurrentWeek = (date) => {
    if (!date) return false;
    const dayOfWeek = date.getDay();
    const sunday = new Date(date);
    sunday.setDate(date.getDate() - dayOfWeek);
    sunday.setHours(0, 0, 0, 0);
    return sunday.getTime() === currentWeekStart.getTime();
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Handle edit button click
  const handleEditClick = (prayerType) => {
    const currentSchedule = prayerSchedules[selectedDay];
    const prayerTimes = currentSchedule?.[prayerType] || [];
    
    setEditModal({
      isOpen: true,
      prayerType,
      prayerTimes
    });
  };

  // Handle edit modal close
  const handleEditModalClose = () => {
    setEditModal({
      isOpen: false,
      prayerType: null,
      prayerTimes: []
    });
  };

  // Handle confirmation modal close
  const handleConfirmModalClose = () => {
    setConfirmModal({
      isOpen: false,
      title: '',
      message: '',
      type: 'default',
      onConfirm: null
    });
  };

  // Handle prayer times update (refresh data)
  const handlePrayerTimesUpdate = async () => {
    await loadPrayerTimes();
  };

  // Helper function to convert 12-hour time to 24-hour format for sorting
  const convertTo24Hour = (time12h) => {
    try {
      const [time, modifier] = time12h.split(' ');
      let [hours, minutes] = time.split(':');
      
      if (hours === '12') {
        hours = '00';
      }
      
      if (modifier === 'PM') {
        hours = parseInt(hours, 10) + 12;
      }
      
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    } catch (error) {
      console.error('Error converting time to 24-hour format:', error);
      return time12h; // Return original if conversion fails
    }
  };

  // Filter prayer times based on search query if provided
  const getFilteredTimes = (times) => {
    let filteredTimes = times;
    
    if (searchQuery && searchQuery.trim()) {
      // If filteredData is provided from parent, use it
      if (filteredData) {
        filteredTimes = times.filter(time => 
          filteredData.some(item => 
            item.id === time.id || 
            (item.time === time.time && (item.title || item.description) === (time.title || time.description))
          )
        );
      } else {
        // Otherwise filter locally
        const query = searchQuery.toLowerCase();
        filteredTimes = times.filter(time => {
          const title = time.title || time.description || '';
          const details = time.details || time.notes || '';
          return (
            time.time.toLowerCase().includes(query) ||
            title.toLowerCase().includes(query) ||
            details.toLowerCase().includes(query)
          );
        });
      }
    }
    
    // Always sort by time in chronological order using shared utility
    return sortItemsByTime(filteredTimes);
  };

  // Delete all prayer times for the selected day
  const handleDeleteSelectedDay = async () => {
    try {
      // Get current day's schedule
      const currentSchedule = prayerSchedules[selectedDay] || {
        shacharit: [],
        mincha: [],
        maariv: []
      };

      // Check if there are any prayer times to delete
      if (!currentSchedule || (!currentSchedule.shacharit?.length && !currentSchedule.mincha?.length && !currentSchedule.maariv?.length)) {
        alert('No prayer times found for the selected day to delete.');
        return;
      }

      // Show confirmation modal
      setConfirmModal({
        isOpen: true,
        title: 'Delete Prayer Times',
        message: `This will remove all prayer times from ${selectedDay}. This action cannot be undone.`,
        type: 'danger',
        onConfirm: async () => {
          handleConfirmModalClose();
          await performDelete();
        }
      });
    } catch (error) {
      console.error('Error in delete preparation:', error);
      alert('Failed to prepare delete operation. Please try again.');
    }
  };

  // Separated the actual delete logic
  const performDelete = async () => {
    try {
      setLoading(true);

      const deleteUpdateData = {
        day: selectedDay,
        shacharit: [],
        mincha: [],
        maariv: []
      };
      
      await prayerService.bulkUpdatePrayerTimes(groupId, deleteUpdateData);

      // Reload prayer times to reflect changes
      await loadPrayerTimes();
      
      alert(`All prayer times deleted for ${selectedDay}.`);
    } catch (error) {
      console.error('Error deleting prayer times:', error);
      alert('Failed to delete prayer times. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Conditional wrapper component for member vs admin view
  const LayoutWrapper = ({ children }) => {
    // Just return children directly - no wrapper needed with new layout
    return <>{children}</>;
  };

  if (loading) {
    return (
      <LayoutWrapper>
        <div className="calendar-page">
          <div className="calendar-container">
            <div className="page-header">
              <div className="page-header-top">
                <div>
                  <h1>Service Schedule</h1>
                  <p className="text-gray-600 text-sm mt-2 font-normal">
                    Create and manage your community's prayer, Shabbat, and holiday service schedules. Updates here sync instantly to the member app.
                  </p>
                </div>
              </div>
            </div>
            <div className="prayer-loading">
              <div className="loading-spinner"></div>
              <p>Loading schedules...</p>
            </div>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  if (error) {
    return (
      <LayoutWrapper>
        <div className="calendar-page">
          <div className="calendar-container">
            <div className="page-header">
              <div className="page-header-top">
                <div>
                  <h1>Service Schedule</h1>
                  <p className="text-gray-600 text-sm mt-2 font-normal">
                    Create and manage your community's prayer, Shabbat, and holiday service schedules. Updates here sync instantly to the member app.
                  </p>
                </div>
              </div>
            </div>
            <div className="prayer-error">
              <p>{error}</p>
              <button className="retry-button" onClick={loadPrayerTimes}>
                Retry
              </button>
            </div>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  // Prayer types for the left column
  const prayerTypes = [
    { 
      key: 'shacharit', 
      label: 'Morning Services',
      sublabel: 'Shacharit',
      icon: 'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Prayer/Shacharit.png'
    },
    { 
      key: 'mincha', 
      label: 'Afternoon Services',
      sublabel: 'Mincha',
      icon: 'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Prayer/Mincha.png'
    },
    { 
      key: 'maariv', 
      label: 'Evening Services',
      sublabel: 'Maariv',
      icon: 'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Prayer/Maariv.png'
    }
  ];

  return (
    <LayoutWrapper>
      <div className="calendar-page">
        <div className="calendar-container">
          {/* Page Header */}
          <div className="page-header">
            <div className="page-header-top">
              <div>
                <h1>Service Schedule</h1>
                <p className="text-gray-600 text-sm mt-2 font-normal">
                  Create and manage your community's prayer, Shabbat, and holiday service schedules. Updates here sync instantly to the member app.
                </p>
              </div>
            </div>
          </div>

          {/* Holiday Filter Slide-in Panel */}
          {showHolidayFilter && (
            <>
              {/* Overlay */}
              <div 
                className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300"
                onClick={() => setShowHolidayFilter(false)}
              />
              
              {/* Slide-in Panel */}
              <div className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-[70] transform translate-x-0 transition-transform duration-300 ease-out ${!filterPanelMounted ? 'animate-slide-in-from-right' : ''}`}>
                <div className="flex flex-col h-full">
                  {/* Panel Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Holiday Filters</h3>
                    <button
                      onClick={() => setShowHolidayFilter(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex gap-2">
                    <button
                      onClick={selectAllFilters}
                      className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      Select All
                    </button>
                    <button
                      onClick={clearAllFilters}
                      className="flex-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                  
                  {/* Filter Options - Checkboxes */}
                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-2">
                      {[
                        { value: 'major', label: 'Major Holidays', emoji: '🕎' },
                        { value: 'minor', label: 'Minor Holidays', emoji: '📖' },
                        { value: 'fast', label: 'Fast Days', emoji: '🙏' },
                        { value: 'rosh_chodesh', label: 'Rosh Chodesh', emoji: '🌙' },
                        { value: 'shabbat', label: 'Special Shabbats', emoji: '🕯️' },
                        { value: 'modern', label: 'Israeli Holidays', emoji: '🇮🇱' }
                      ].map(({ value, label, emoji }) => (
                        <label
                          key={value}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                            selectedHolidayFilters.includes(value)
                              ? 'bg-amber-50 border-2 border-amber-400'
                              : 'bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedHolidayFilters.includes(value)}
                            onChange={() => toggleHolidayFilter(value)}
                            className="w-5 h-5 text-amber-600 bg-white border-gray-300 rounded focus:ring-2 focus:ring-amber-500 cursor-pointer"
                          />
                          <span className="text-xl">{emoji}</span>
                          <span className={`font-medium text-sm ${selectedHolidayFilters.includes(value) ? 'text-amber-700' : 'text-gray-700'}`}>
                            {label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div className="p-4 border-t border-gray-200 bg-white">
                    <button
                      onClick={() => setShowHolidayFilter(false)}
                      className="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        
          {/* Week Navigation - Hidden for non-admins in desktop view */}
          {isAdmin && (
          <div className="flex items-center justify-between mb-6 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-200">
            <button
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={goToPreviousWeek}
              disabled={isAnimating}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              Previous Week
            </button>
            
            <div className="flex items-center gap-3">
              {!isCurrentWeek() && (
                <button
                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={goToCurrentWeek}
                  disabled={isAnimating}
                >
                  Today
                </button>
              )}
              <span className="text-lg font-semibold text-gray-900">
                {getWeekRangeDisplay()}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={goToNextWeek}
                disabled={isAnimating}
              >
                Next Week
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              
              {/* Mini Calendar Toggle */}
              <div className="relative">
                <button
                  id="mini-calendar-button"
                  className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                  onClick={() => setShowMiniCalendar(!showMiniCalendar)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
                
                {/* Mini Calendar Dropdown */}
                {showMiniCalendar && (
                  <div 
                    id="mini-calendar-dropdown"
                    className="absolute right-0 mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 p-5 z-50 w-96"
                  >
                    {/* Calendar Header */}
                    <div className="flex items-center justify-between mb-4">
                      <button
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        onClick={goToPreviousMonth}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} className="text-gray-600" />
                      </button>
                      <span className="font-semibold text-gray-900 text-base">
                        {miniCalendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button
                        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                        onClick={goToNextMonth}
                      >
                        <FontAwesomeIcon icon={faChevronRight} className="text-gray-600" />
                      </button>
                    </div>
                    
                    {/* Day Labels */}
                    <div className="grid grid-cols-7 gap-1.5 mb-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                        <div key={i} className="text-center text-xs font-semibold text-gray-600 py-1">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 gap-1.5">
                      {getDaysInMonth(miniCalendarMonth).map((date, index) => {
                        const holidays = date ? getMiniCalendarHolidaysForDate(date) : [];
                        const hasHoliday = holidays.length > 0;
                        const holidayName = hasHoliday ? holidays[0].name : '';
                        return (
                          <button
                            key={index}
                            onClick={() => handleDateClick(date)}
                            disabled={!date}
                            className={`
                              h-14 flex flex-col items-center justify-center text-sm rounded-lg transition-all relative p-1
                              ${!date ? 'invisible' : ''}
                              ${isToday(date) ? 'bg-blue-600 text-white font-bold hover:bg-blue-700' : ''}
                              ${isDateInCurrentWeek(date) && !isToday(date) ? 'bg-blue-100 text-blue-700 font-medium' : ''}
                              ${!isToday(date) && !isDateInCurrentWeek(date) && hasHoliday ? 'bg-amber-50 text-amber-900 font-medium hover:bg-amber-100' : ''}
                              ${!isToday(date) && !isDateInCurrentWeek(date) && !hasHoliday ? 'hover:bg-gray-100 text-gray-700' : ''}
                            `}
                            title={hasHoliday ? holidays.map(h => h.name).join(', ') : ''}
                          >
                            <span className="font-semibold">{date ? date.getDate() : ''}</span>
                            {hasHoliday && (
                              <span className={`text-[9px] leading-tight truncate w-full text-center ${
                                isToday(date) ? 'text-blue-100' : 'text-amber-600'
                              }`}>
                                {holidayName.length > 10 ? holidayName.substring(0, 9) + '...' : holidayName}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Holiday Filter Toggle */}
              <button
                className={`relative flex items-center gap-2 px-3 py-2 rounded-lg transition-colors font-medium ${
                  selectedHolidayFilters.length > 0 && selectedHolidayFilters.length < 6
                    ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                    : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                }`}
                onClick={() => setShowHolidayFilter(true)}
                title={`${selectedHolidayFilters.length} holiday types selected`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {selectedHolidayFilters.length > 0 && selectedHolidayFilters.length < 6 && (
                  <span className="absolute -top-1 -right-1 px-1.5 min-w-[1.25rem] h-5 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {selectedHolidayFilters.length}
                  </span>
                )}
              </button>
            </div>
          </div>
          )}

          {/* Weekly Calendar Grid View */}
          <div 
            className={`bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden ${
              slideDirection === 'left' ? 'animate-slide-out-left' :
              slideDirection === 'right' ? 'animate-slide-out-right' :
              slideDirection === 'left-in' ? 'animate-slide-in-from-right' :
              slideDirection === 'right-in' ? 'animate-slide-in-from-left' :
              ''
            }`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
          {/* Calendar Header - Days of Week */}
          <div className="grid grid-cols-7 border-b border-gray-200">
            {/* Day headers */}
            {daysOfWeek.map((day) => {
              const isToday = day.name === getTodayDayName() && isCurrentWeek();
              return (
                <div
                  key={day.name}
                  className={`relative flex flex-col items-center justify-center py-4 px-2 border-r border-gray-200 last:border-r-0 transition-all duration-200 cursor-pointer ${
                    isToday 
                      ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' 
                      : 'bg-gradient-to-br from-gray-50 to-white hover:bg-blue-50'
                  }`}
                  onClick={() => {
                    if (isAdmin) {
                      setSelectedDayForModal(day.name);
                      setDayModalOpen(true);
                    }
                  }}
                >
                  <div className="text-center">
                    <div className={`font-bold text-sm mb-0.5 ${isToday ? 'text-white' : 'text-gray-900'}`}>
                      {day.name}
                    </div>
                    <div className={`text-xs font-medium ${isToday ? 'text-blue-100' : 'text-gray-500'}`}>
                      {day.date.display}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Calendar Body - All Items by Day (Chronological) */}
          <div className="grid grid-cols-7">
            {daysOfWeek.map((day) => {
              const daySchedule = prayerSchedules[day.name] || {};
              
              // Check if this day has any holidays
              const dayDate = getFullDateForDay(day.name);
              const dayHolidays = getHolidaysForDate(dayDate);
              const hasHoliday = dayHolidays.length > 0;
              
              // Flatten all prayer types into single chronological list
              const allItems = [];
              prayerTypes.forEach(prayerType => {
                const times = daySchedule[prayerType.key] || [];
                const filteredTimes = getFilteredTimes(times);
                filteredTimes.forEach(time => {
                  allItems.push({
                    ...time,
                    prayerType: prayerType.key,
                    prayerLabel: prayerType.sublabel
                  });
                });
              });
              
              // Sort by time using shared utility
              const sortedItems = sortItemsByTime(allItems);

              const isToday = day.name === getTodayDayName();

              return (
                <div
                  key={day.name}
                  className={`border-r border-gray-200 last:border-r-0 p-3 min-h-[400px] transition-all duration-200 ${
                    isToday 
                      ? 'bg-blue-50' 
                      : hasHoliday 
                        ? 'bg-amber-50 hover:bg-amber-100' 
                        : 'bg-white hover:bg-gray-50'
                  } ${isAdmin ? 'cursor-pointer' : ''}`}
                  onClick={() => {
                    if (isAdmin) {
                      setSelectedDayForModal(day.name);
                      setDayModalOpen(true);
                    }
                  }}
                >
                  {/* Holiday Badge */}
                  {hasHoliday && (
                    <div className="mb-2 space-y-1">
                      {dayHolidays.map((holiday, idx) => (
                        <div 
                          key={idx}
                          className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-1 rounded-md border border-amber-200"
                          title={holiday.hebrew_name}
                        >
                          ✨ {holiday.name}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Agenda Items List */}
                  <div className="space-y-2">
                    {sortedItems.length > 0 ? (
                      sortedItems.map((item, index) => (
                        <div
                          key={`${item.id || index}`}
                          className={`p-2 rounded-lg border transition-all duration-200 group ${
                            isToday 
                              ? 'bg-white border-blue-200 hover:border-blue-400 hover:shadow-md' 
                              : 'bg-gray-50 border-gray-200 hover:border-blue-300 hover:shadow-md'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDayForModal(day.name);
                            setDayModalOpen(true);
                          }}
                        >
                          <div className="font-bold text-blue-600 text-sm mb-0.5 group-hover:text-blue-700">
                            {item.title || item.description}
                          </div>
                          {item.details && (
                            <div className="text-xs text-gray-600 mb-1 line-clamp-1 group-hover:text-gray-700">
                              {item.details}
                            </div>
                          )}
                          <div className="text-xs text-gray-900 font-medium group-hover:text-gray-800">
                            {item.zmanim_rule_enabled && '~ '}
                            {item.time}
                          </div>
                          {item.zmanim_rule_enabled && (
                            <div className="mt-1 text-xs text-purple-600 font-medium">
                              {(item.zmanim_offset_minutes || 0) > 0 ? `${item.zmanim_offset_minutes} min ` : ''}
                              {item.zmanim_offset_direction || 'before'}{' '}
                              {(() => {
                                const typeMap = {
                                  'sunrise': 'Sunrise',
                                  'sunset': 'Sunset',
                                  'sofZmanShma': 'Latest Shema',
                                  'sofZmanTfilla': 'Latest Shacharit',
                                  'chatzot': 'Midday',
                                  'minchaGedola': 'Mincha Gedola',
                                  'minchaKetana': 'Mincha Ketana',
                                  'plagHaMincha': 'Plag HaMincha',
                                  'alotHaShachar': 'Alot HaShachar',
                                  'misheyakir': 'Misheyakir',
                                  'tzeit7083deg': 'Tzeit (3 stars)',
                                  'tzeit72min': 'Tzeit (Rabbeinu Tam)'
                                };
                                return typeMap[item.zmanim_type] || item.zmanim_type || 'sunset';
                              })()}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-gray-400 italic text-center py-12">
                        {isAdmin ? (
                          <div className="flex flex-col items-center gap-1">
                            <span>No items scheduled</span>
                            <span className="text-blue-500 font-medium">Click to add</span>
                          </div>
                        ) : (
                          'No items scheduled'
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Prayer Edit Modal */}
        <PrayerEditModal
          isOpen={editModal.isOpen}
          onClose={handleEditModalClose}
          groupId={groupId}
          selectedDay={selectedDay}
          prayerType={editModal.prayerType}
          prayerTimes={editModal.prayerTimes}
          onPrayerTimesUpdate={handlePrayerTimesUpdate}
        />

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmModal.isOpen}
          onClose={handleConfirmModalClose}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          confirmText={confirmModal.type === 'danger' ? 'Delete' : 'Confirm'}
          cancelText="Cancel"
          isLoading={loading}
        />

        {/* Day Agenda Panel */}
        <DayAgendaPanel
          isOpen={dayModalOpen}
          onClose={() => setDayModalOpen(false)}
          groupId={groupId}
          selectedDate={selectedDayForModal ? getFullDateForDay(selectedDayForModal) : new Date()}
          dayName={selectedDayForModal || ''}
          allPrayerTimes={selectedDayForModal ? (prayerSchedules[selectedDayForModal] || {}) : {}}
          onPrayerTimesUpdate={reloadPrayerTimesQuietly}
          holidays={selectedDayForModal ? getHolidaysForDate(getFullDateForDay(selectedDayForModal)) : []}
        />
      </div>
    </div>
    </LayoutWrapper>
  );
};

export default Prayer;